import React from "react";
import { Container } from "react-bootstrap/";
import "./WorkExperience.css";
import { VerticalTimeline, VerticalTimelineElement } from 'react-vertical-timeline-component';
import 'react-vertical-timeline-component/style.min.css';
import Resume from "../Assets/Divyansh Goyal's Resume.pdf";

function TechnicalExperience2() {
  const experiences = [
    {
      role: "Application Developer - Cloud & AI Developer",
      company: "IBM",
      location: "Full-Time",
      date: "Sept, 2025 - Present",
      metrics: [
        { label: "🤖 Agentic AI", type: "purple" },
        { label: "💻 Full-Stack Development", type: "blue" },
        { label: "📊 Data Engineering", type: "cyan" },
      ],
      points: [
        "Engineered production-grade agentic AI systems across government and insurance sectors: a GPT-powered voice agent with 10+ function tools integrated with Genesys Cloud for real-time insurance automation, and two Azure OpenAI agents that generate technical documentation from source code using Kernel-Memory RAG.",
        "Delivered full-stack enterprise solutions for government portals using Angular/NgRx with JWT & Azure AD MSAL authentication, maintaining 100% test coverage and full Ontario Design System compliance across profile management systems.",
        "Built an end-to-end Business Document Processing AI Agent that ingests complex technical architecture documents to automatically generate stakeholder-friendly requirements documentation.",
      ],
      skills: ["Python", "TypeScript", "Azure OpenAI", "RAG Pipeline", "AngularJS", "Snowflake", "Cloud Infrastructure"],
    },
    {
      role: "Technology Analyst Intern",
      company: "BMO (Bank of Montreal)",
      location: "Internship",
      date: "May, 2024 - Aug, 2024",
      metrics: [
        { label: "⚡ 50% Deployment Speedup", type: "blue" },
        { label: "📉 30% Defect Reduction", type: "cyan" },
        { label: "📊 Executive Power BI Dashboard", type: "gold" },
      ],
      points: [
        "Leveraged Power BI to architect an executive dashboard showcasing DORA metrics, team KPIs, workload distribution, and multi-product velocity with automated real-time data refreshes.",
        "Engineered dynamic, source-selective unit testing within non-production and production pipelines, reducing deployment times by >50% and cutting UAT and production defects by 30%.",
      ],
      skills: ["Power BI", "CI/CD Pipelines", "Automated Testing", "Ansible", "SQL", "Agile DevOps"],
    },
    {
      role: "DevOps Developer Intern",
      company: "IBM",
      location: "Internship (16 Months)",
      date: "May, 2022 - Aug, 2023",
      metrics: [
        { label: "⏱️ 400+ Hours Saved", type: "cyan" },
        { label: "⚙️ Jenkins Automation", type: "purple" },
      ],
      points: [
        "Designed and deployed automated Jenkins pipeline solutions to optimize IBM Cloud Pak documentation workflows using Python, Batch, and Shell scripting.",
        "Engineered high-throughput batch update scripts for XML-based DITA source files, conserving over 400 hours of manual technical writing labor.",
      ],
      skills: ["Python", "Jenkins", "Shell Scripting", "IBM Cloud Pak", "Git", "DITA XML"],
    },
    {
      role: "Undergraduate Student Researcher (First Author)",
      company: "Biometric Technologies Laboratory",
      location: "Research Fellowship",
      date: "May, 2021 - Aug, 2021",
      metrics: [
        { label: "📄 IEEE 1st-Author Publication", type: "gold" },
        { label: "🔥 PyTorch Deep Learning", type: "purple" },
      ],
      points: [
        "Designed and trained deep attribute classification models in PyTorch (Python) for person re-identification in surveillance imagery.",
        "Primary author of scientific paper published in IEEE SSCI 2021 (IEEE Symposium Series on Computational Intelligence), establishing a benchmark framework for explainable AI models.",
      ],
      paperLink: "https://ieeexplore.ieee.org/document/9660071",
      skills: ["PyTorch", "Python", "Computer Vision", "Deep Learning", "Scikit-Learn", "IEEE Research"],
    },
  ];

  return (
    <Container fluid className="workExperienceSection py-6" id="technicalExperience">
      <Container>
        <div className="section-header text-center">
          <h2 className="section-title">Technical Work Experience</h2>
          <div className="titleHr" />

          <div className="mt-3 mb-4">
            <a
              href={Resume}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary-custom"
            >
              📄 View Resume PDF
            </a>
          </div>
        </div>

        <VerticalTimeline lineColor="rgba(59, 130, 246, 0.3)">
          {experiences.map((exp, idx) => (
            <VerticalTimelineElement
              key={idx}
              className="vertical-timeline-element--work"
              contentStyle={{
                background: "rgba(17, 24, 39, 0.75)",
                backdropFilter: "blur(16px)",
                border: "1px solid rgba(255, 255, 255, 0.1)",
                borderRadius: "16px",
                boxShadow: "0 10px 30px rgba(0, 0, 0, 0.4)",
                color: "#ffffff",
              }}
              contentArrowStyle={{ borderRight: "7px solid rgba(59, 130, 246, 0.6)" }}
              date={exp.date}
              dateClassName="timelineDate"
              iconStyle={{
                background: "linear-gradient(135deg, #2563eb, #06b6d4)",
                boxShadow: "0 0 15px rgba(37, 99, 235, 0.6)",
                border: "3px solid #ffffff",
              }}
            >
              <div className="timelineHeader">
                <h3 className="vertical-timeline-element-title roleTitle">{exp.role}</h3>
                <h4 className="vertical-timeline-element-subtitle companyName">{exp.company}</h4>
              </div>

              {/* Metric Badges */}
              <div className="timelineMetrics mt-2 mb-3">
                {exp.metrics.map((m, mIdx) => (
                  <span
                    key={mIdx}
                    className={`badge-pill ${m.type === "cyan"
                      ? "badge-pill-cyan"
                      : m.type === "purple"
                        ? "badge-pill-purple"
                        : m.type === "gold"
                          ? "badge-pill-gold"
                          : ""
                      }`}
                  >
                    {m.label}
                  </span>
                ))}
              </div>

              {/* Bullet Points */}
              <ul className="timelineBullets">
                {exp.points.map((pt, pIdx) => (
                  <li key={pIdx}>{pt}</li>
                ))}
              </ul>

              {exp.paperLink && (
                <div className="mt-2 mb-3">
                  <a
                    href={exp.paperLink}
                    target="_blank"
                    rel="noreferrer"
                    className="IEEElink"
                  >
                    🔗 IEEE Symposium Publication Link &rarr;
                  </a>
                </div>
              )}

              {/* Skill Tags */}
              <div className="timelineSkillTags mt-3">
                {exp.skills.map((s, sIdx) => (
                  <span key={sIdx} className="skillChip">
                    {s}
                  </span>
                ))}
              </div>
            </VerticalTimelineElement>
          ))}
        </VerticalTimeline>
      </Container>
    </Container>
  );
}

export default TechnicalExperience2;
